import type { UserProfile } from '@/lib/auth/client';

/**
 * Whether this profile still needs to verify its email before the signup funnel
 * can continue. Single source of truth for the funnel's "show the email step?"
 * decision — consumed by both the signup sheet (skip to terms or hand off) and
 * the app-level email-verification page (self-gate).
 *
 * Strict `=== false`: a social login arrives with no `email` (nothing to verify)
 * or `isEmailVerified === true`, so only an email/password account Firebase has
 * positively marked unverified needs the step.
 */
export function needsEmailVerification(user: UserProfile | null): boolean {
  return !!user?.email && user.isEmailVerified === false;
}

/**
 * Whether a player has completed signup onboarding, gated on the server-tracked
 * email-verification flag carried in the JWT claims.
 *
 * Onboarding now completes at email verification: a player with a session and a
 * verified email — or a social login (Google) with no email to verify — is
 * considered fully onboarded and gets full app access. Address and phone are no
 * longer part of the signup funnel; they are collected later, on demand, by
 * their own gates (deposit / KYC) and so do not block onboarding here.
 *
 * Single source of truth for "is onboarding done?". Note it is stricter than
 * {@link needsEmailVerification}: it treats a falsy `isEmailVerified` (incl.
 * `undefined`) as not-yet-onboarded, so an email-bearing profile stays gated
 * until the claim is positively `true`.
 */
export function isOnboardingComplete(user: UserProfile | null): boolean {
  if (!user) return false;
  // Email/password signups must verify their email; social logins arrive with
  // no `email` (nothing to verify) or `isEmailVerified === true`.
  if (user.email && !user.isEmailVerified) return false;
  return true;
}
